import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from './config';
// eslint-disable-next-line camelcase
import jwt_decode from 'jwt-decode';

let storedAccessToken = localStorage.getItem('accessToken');

let decodedEmail = '';
let decodePhone = '';
let decodedRole = '';
if (storedAccessToken) {
  try {
    const decodedToken = jwt_decode(storedAccessToken);

    decodedEmail = decodedToken.email;
    decodePhone = decodedToken.phone;
    decodedRole = decodedToken.role;
  } catch (error) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('role');
    storedAccessToken = '';
  }
}

const initialState = {
  accessToken: storedAccessToken || '',
  refreshToken: localStorage.getItem('refreshToken') || '',
  profile: {},
  loading: false,
  isSuccess: false,
  errors: [],
  role: decodedRole,
  menuCategoryOptions: []
};

export const signin = createAsyncThunk(
  'auth/signin',
  async (body, { rejectWithValue }) => {
    try {
      const params = { email: body.email, password: body.password };
      const response = await api.post('/auth/signin', params);

      // Store the data in localStorage
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      const decoded = jwt_decode(response.data.accessToken);
      localStorage.setItem('role', decoded.role);

      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const getProfile = createAsyncThunk(
  'user/profile',
  async (__, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/user/profile`);
      // localStorage.setItem("company",response.data.company._id)
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'user/updateprofile',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        email: body.email,
        phone: body.phone,
        firstname: body.firstname,
        lastname: body.lastname
      };
      const response = await api.put(`/user/profile`, params);
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePassword = createAsyncThunk(
  'user/updatePassword',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        old_password: body.old_password,
        password: body.password,
        passwordConfirm: body.passwordConfirm
      };
      const response = await api.put(`/user/change-password`, params);
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const UpdateCompany = createAsyncThunk(
  'profile/updateCompany',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.put('/company', formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const DeleteCompanyMedia = createAsyncThunk(
  'company/DeletMedia',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/company/media/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
// ===== MENU SECTIONS =====

// Create menu section
export const CreateMenuSection = createAsyncThunk(
  'company/createMenuSection',
  async ({ sectionTitle }, { rejectWithValue }) => {
    try {
      const response = await api.post('/company/menu-section', {
        sectionTitle
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update menu section title
export const UpdateMenuSectionTitle = createAsyncThunk(
  'company/updateMenuSectionTitle',
  async ({ sectionId, sectionTitle }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/company/menu-section/${sectionId}`, {
        sectionTitle
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete menu section
export const DeleteMenuSection = createAsyncThunk(
  'company/deleteMenuSection',
  async (sectionId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/company/menu-section/${sectionId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Upload image to menu section
export const UploadMenuSectionImage = createAsyncThunk(
  'company/uploadMenuSectionImage',
  async ({ sectionId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/company/menu-section/${sectionId}/image`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete image from menu section
export const DeleteMenuSectionImage = createAsyncThunk(
  'company/deleteMenuSectionImage',
  async ({ sectionId, imageId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/company/menu-section/${sectionId}/image/${imageId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== RESTAURANT MENU ====================

// Get the category templates defined on this company's business area
// (returns [{ key, label: {fr,en,ar,...}, image, order }], sorted by order)
export const GetMenuCategoryOptions = createAsyncThunk(
  'company/getMenuCategoryOptions',
  async (__, { rejectWithValue }) => {
    try {
      const response = await api.get(
        '/company/restaurant-menu/category-options'
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Open (enable) a menu category
export const CreateMenuCategory = createAsyncThunk(
  'company/createMenuCategory',
  async ({ categoryKey }, { rejectWithValue }) => {
    try {
      const response = await api.post('/company/restaurant-menu/category', {
        categoryKey
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Open / close a menu category
export const UpdateMenuCategoryStatus = createAsyncThunk(
  'company/updateMenuCategoryStatus',
  async ({ categoryKey, isActive }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/company/restaurant-menu/category/${categoryKey}/status`,
        { isActive }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a menu category
export const DeleteMenuCategory = createAsyncThunk(
  'company/deleteMenuCategory',
  async (categoryKey, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/company/restaurant-menu/category/${categoryKey}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add a dish to a category
export const AddDish = createAsyncThunk(
  'company/addDish',
  async ({ categoryKey, formData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/company/restaurant-menu/category/${categoryKey}/dish`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update a dish
export const UpdateDish = createAsyncThunk(
  'company/updateDish',
  async ({ categoryKey, dishId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/company/restaurant-menu/category/${categoryKey}/dish/${dishId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a dish
export const DeleteDish = createAsyncThunk(
  'company/deleteDish',
  async ({ categoryKey, dishId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/company/restaurant-menu/category/${categoryKey}/dish/${dishId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Switch the restaurant menu mode ('manual' | 'image')
export const SwitchMenuMode = createAsyncThunk(
  'company/switchMenuMode',
  async ({ menuMode }, { rejectWithValue }) => {
    try {
      const response = await api.put('/company/restaurant-menu/mode', {
        menuMode
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Enable/disable service requests (room service for hotels, waiter calls for other business areas)
export const SwitchServiceRequestsEnabled = createAsyncThunk(
  'company/switchServiceRequestsEnabled',
  async ({ serviceRequestsEnabled }, { rejectWithValue }) => {
    try {
      const response = await api.patch('/company/service-requests/status', {
        serviceRequestsEnabled
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Upload a menu photo (image mode)
export const UploadMenuPhoto = createAsyncThunk(
  'company/uploadMenuPhoto',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        '/company/restaurant-menu/photos',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a menu photo (image mode)
export const DeleteMenuPhoto = createAsyncThunk(
  'company/deleteMenuPhoto',
  async (imageId, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/company/restaurant-menu/photos/${imageId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== BEST DEALS ====================

export const AddBestDeal = createAsyncThunk(
  'company/addBestDeal',
  async ({ dealData, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.entries(dealData).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });
      if (file) formData.append('file', file);
      const response = await api.post('/company/best-deals', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const UpdateBestDeal = createAsyncThunk(
  'company/updateBestDeal',
  async ({ dealId, dealData, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.entries(dealData).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value);
        }
      });
      if (file) formData.append('file', file);
      const response = await api.put(`/company/best-deals/${dealId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const DeleteBestDeal = createAsyncThunk(
  'company/deleteBestDeal',
  async (dealId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/company/best-deals/${dealId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const UploadBestDealImage = createAsyncThunk(
  'company/uploadBestDealImage',
  async ({ dealId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/company/best-deals/${dealId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== GALLERY IMAGES ====================
export const AddCompanyGalleryImage = createAsyncThunk(
  'company/AddGalleryImage',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/company/gallery-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log(response);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const DeleteCompanyGalleryImage = createAsyncThunk(
  'company/DeleteGalleryImage',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/company/gallery-image/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ==================== COVERS ====================
export const DeleteCompanyCover = createAsyncThunk(
  'company/DeleteCover',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/company/couverture/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const AddCompanyCover = createAsyncThunk(
  'company/AddCover',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/company/couverture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ==================== MEDIA ====================
export const AddCompanyMedia = createAsyncThunk(
  'company/AddMedia',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/company/media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ==================== MAIN PICTURE ====================
export const AddCompanyMainPic = createAsyncThunk(
  'company/AddMainPic',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/company/photoprincipal', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const DeleteCompanyMainPic = createAsyncThunk(
  'company/DeleteMainPic',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/company/photoprincipal/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

// ==================== LOST ITEMS ====================
export const AddCompanyLostItem = createAsyncThunk(
  'company/AddLostItem',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/company/lost-item', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const DeleteCompanyLostItem = createAsyncThunk(
  'company/DeleteLostItem',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/company/lost-item/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error);
    }
  }
);

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${process.env.REACT_APP_COOKIES_HOST}; path=/;`;
}

// user
const authSlice = createSlice({
  name: 'superadmin',
  initialState,
  reducers: {
    logout: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.errors = [];
      state.profile = {};
      localStorage.clear();
      deleteCookie('accessToken');
      deleteCookie('refreshToken');
      window.location.href = `${process.env.REACT_APP_ADMIN_URL}/login`;
    }
  },
  extraReducers: {
    [signin.pending]: (state) => {
      state.loading = true;
    },
    [signin.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      if (!payload?.accessToken || !payload?.refreshToken) {
        state.isSuccess = false;
        state.errors = ['AccessToken and/or refreshToken not found'];
      } else {
        state.accessToken = payload.accessToken;
        state.refreshToken = payload.refreshToken;
        const decodedToken = jwt_decode(payload.accessToken);
        state.email = decodedToken.email;
        state.role = decodedToken.role;
      }
      if (
        state.role !== 'ADMIN' ||
        state.role !== 'MANAGER' ||
        state.role !== 'RESPONSIBLE'
      ) {
        state.errors = ['Without authorization, you do not have privileges.'];
      }

      state.loading = false;
    },
    [signin.rejected]: (state, { payload }) => {
      state.errors = payload?.message;
      state.isSuccess = false;
      state.loading = true;
    },
    [getProfile.pending]: (state) => {
      state.loading = true;
    },
    [getProfile.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile = payload;
      state.loading = false;
    },
    [getProfile.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [updateProfile.pending]: (state) => {
      state.loading = true;
    },
    [updateProfile.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      console.log(state.profile)
      console.log("payload**************************************")
      console.log(payload);
      state.profile = { ...state.profile, ...payload };
      state.loading = false;
    },
    [updateProfile.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [updatePassword.pending]: (state) => {
      state.loading = true;
    },
    [updatePassword.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
    },
    [updatePassword.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [DeleteCompanyMedia.pending]: (state) => {
      state.loading = true;
    },
    [DeleteCompanyMedia.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteCompanyMedia.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [DeleteCompanyCover.pending]: (state) => {
      state.loading = true;
    },
    [DeleteCompanyCover.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteCompanyCover.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    // ==================== GALLERY IMAGES ====================
    [AddCompanyGalleryImage.pending]: (state) => {
      state.loading = true;
    },
    [AddCompanyGalleryImage.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [AddCompanyGalleryImage.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [DeleteCompanyGalleryImage.pending]: (state) => {
      state.loading = true;
    },
    [DeleteCompanyGalleryImage.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteCompanyGalleryImage.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UpdateCompany.pending]: (state) => {
      state.loading = true;
    },
    [UpdateCompany.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UpdateCompany.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [AddCompanyMedia.pending]: (state) => {
      state.loading = true;
    },
    [AddCompanyMedia.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [AddCompanyMedia.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [AddCompanyMainPic.pending]: (state) => {
      state.loading = true;
    },
    [AddCompanyMainPic.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [AddCompanyMainPic.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [AddCompanyCover.pending]: (state) => {
      state.loading = true;
    },
    [AddCompanyCover.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [AddCompanyCover.rejected]: (state, { payload }) => {
      if (payload.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [DeleteCompanyMainPic.pending]: (state) => {
      state.loading = true;
    },
    [DeleteCompanyMainPic.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteCompanyMainPic.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) state.errors = payload?.message;
      state.isSuccess = false;
      state.loading = false;
    },
    [AddCompanyLostItem.pending]: (state) => {
      state.loading = true;
    },
    [AddCompanyLostItem.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [AddCompanyLostItem.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) state.errors = payload?.message;
      state.isSuccess = false;
      state.loading = false;
    },

    [DeleteCompanyLostItem.pending]: (state) => {
      state.loading = true;
    },
    [DeleteCompanyLostItem.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteCompanyLostItem.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) state.errors = payload?.message;
      state.isSuccess = false;
      state.loading = false;
    },
    [CreateMenuSection.pending]: (state) => {
      state.loading = true;
    },
    [CreateMenuSection.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [CreateMenuSection.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UpdateMenuSectionTitle.pending]: (state) => {
      state.loading = true;
    },
    [UpdateMenuSectionTitle.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UpdateMenuSectionTitle.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [DeleteMenuSection.pending]: (state) => {
      state.loading = true;
    },
    [DeleteMenuSection.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteMenuSection.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UploadMenuSectionImage.pending]: (state) => {
      state.loading = true;
    },
    [UploadMenuSectionImage.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UploadMenuSectionImage.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [DeleteMenuSectionImage.pending]: (state) => {
      state.loading = true;
    },
    [DeleteMenuSectionImage.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteMenuSectionImage.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [AddBestDeal.pending]: (state) => {
      state.loading = true;
    },
    [AddBestDeal.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [AddBestDeal.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UpdateBestDeal.pending]: (state) => {
      state.loading = true;
    },
    [UpdateBestDeal.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UpdateBestDeal.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [DeleteBestDeal.pending]: (state) => {
      state.loading = true;
    },
    [DeleteBestDeal.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteBestDeal.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UploadBestDealImage.pending]: (state) => {
      state.loading = true;
    },
    [UploadBestDealImage.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UploadBestDealImage.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [GetMenuCategoryOptions.fulfilled]: (state, { payload }) => {
      state.menuCategoryOptions = payload;
    },

    [CreateMenuCategory.pending]: (state) => {
      state.loading = true;
    },
    [CreateMenuCategory.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [CreateMenuCategory.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UpdateMenuCategoryStatus.pending]: (state) => {
      state.loading = true;
    },
    [UpdateMenuCategoryStatus.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UpdateMenuCategoryStatus.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [DeleteMenuCategory.pending]: (state) => {
      state.loading = true;
    },
    [DeleteMenuCategory.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteMenuCategory.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [AddDish.pending]: (state) => {
      state.loading = true;
    },
    [AddDish.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [AddDish.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UpdateDish.pending]: (state) => {
      state.loading = true;
    },
    [UpdateDish.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UpdateDish.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [DeleteDish.pending]: (state) => {
      state.loading = true;
    },
    [DeleteDish.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteDish.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [SwitchMenuMode.pending]: (state) => {
      state.loading = true;
    },
    [SwitchMenuMode.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [SwitchMenuMode.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [SwitchServiceRequestsEnabled.pending]: (state) => {
      state.loading = true;
    },
    [SwitchServiceRequestsEnabled.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [SwitchServiceRequestsEnabled.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [UploadMenuPhoto.pending]: (state) => {
      state.loading = true;
    },
    [UploadMenuPhoto.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [UploadMenuPhoto.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },

    [DeleteMenuPhoto.pending]: (state) => {
      state.loading = true;
    },
    [DeleteMenuPhoto.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.profile.company = payload;
      state.loading = false;
    },
    [DeleteMenuPhoto.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    }
  }
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
